import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ChevronLeft, 
  ChevronRight, 
  Save, 
  X, 
  AlertCircle,
  RefreshCw,
  Calendar,
  Plus,
  Trash2,
  Edit2,
  RotateCcw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatCurrencyInput, parseCurrencyInput } from "../utils/currencyFormat";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";

import LastUpdated from "../components/LastUpdated";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const RepaymentsPage = () => {
  const { yearParam } = useParams();
  const navigate = useNavigate();
  
  const currentYear = new Date().getFullYear();
  
  const [year, setYear] = useState(() => {
    if (yearParam) {
      const parsed = parseInt(yearParam);
      if (!isNaN(parsed) && parsed >= 2000 && parsed <= 2100) {
        return parsed;
      }
    }
    return currentYear;
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [plans, setPlans] = useState([]);
  const [entries, setEntries] = useState([]);
  const [originalEntries, setOriginalEntries] = useState([]);
  const [modifiedEntries, setModifiedEntries] = useState({});
  
  // Dialog states
  const [showAddPlan, setShowAddPlan] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    creditor_name: "",
    installment_amount: "",
    num_terms: "12",
    frequency: "monthly",
    start_date: `${currentYear}-01-01`
  });

  useEffect(() => { document.title = "Repayments - Floatly"; }, []);

  // Update URL when year changes
  useEffect(() => {
    const newPath = `/repayments/${year}`;
    if (window.location.pathname !== newPath) {
      navigate(newPath, { replace: true });
    }
  }, [year, navigate]);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setModifiedEntries({});
    try {
      const response = await axios.get(`${API}/repayments/${year}`);
      setPlans(response.data.plans);
      setEntries(response.data.entries);
      setOriginalEntries(JSON.parse(JSON.stringify(response.data.entries)));
    } catch (err) {
      console.error("Error fetching repayments:", err);
      setError("Failed to load repayment data");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check if there are changes
  const hasChanges = Object.keys(modifiedEntries).length > 0;

  // Navigation
  const goToPrevYear = () => {
    if (hasChanges && !window.confirm("You have unsaved changes. Discard and navigate?")) {
      return;
    }
    setYear(y => y - 1);
  };

  const goToNextYear = () => {
    if (hasChanges && !window.confirm("You have unsaved changes. Discard and navigate?")) {
      return;
    }
    setYear(y => y + 1);
  };

  const goToCurrentYear = () => {
    if (hasChanges && !window.confirm("You have unsaved changes. Discard and navigate?")) {
      return;
    }
    setYear(currentYear);
  };

  // Generate all dates for the year
  const allDates = useMemo(() => {
    const dates = [];
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split('T')[0]);
    }
    return dates;
  }, [year]);

  // Build entry lookup map
  const entryMap = useMemo(() => {
    const map = {};
    entries.forEach(entry => {
      const key = `${entry.plan_id}_${entry.date}`;
      map[key] = entry;
    });
    // Apply modifications
    Object.entries(modifiedEntries).forEach(([key, value]) => {
      if (map[key]) {
        map[key] = { ...map[key], amount: value, is_manual_override: true };
      }
    });
    return map;
  }, [entries, modifiedEntries]);

  // Calculate totals per date
  const dateTotals = useMemo(() => {
    const totals = {};
    allDates.forEach(dateStr => {
      let total = 0;
      plans.forEach(plan => {
        const key = `${plan.id}_${dateStr}`;
        const entry = entryMap[key];
        if (entry) {
          total += parseFloat(entry.amount) || 0;
        }
      });
      if (total > 0) {
        totals[dateStr] = total;
      }
    });
    return totals;
  }, [allDates, plans, entryMap]);

  // Filter dates with payments
  const datesWithPayments = useMemo(() => {
    return allDates.filter(d => dateTotals[d] > 0);
  }, [allDates, dateTotals]);

  // Annual total
  const annualTotal = useMemo(() => {
    return Object.values(dateTotals).reduce((sum, val) => sum + val, 0);
  }, [dateTotals]);

  // Handle amount change
  const handleAmountChange = (planId, dateStr, value) => {
    const key = `${planId}_${dateStr}`;
    setModifiedEntries(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
  };

  // Reset plan form
  const resetPlanForm = () => {
    setPlanForm({
      creditor_name: "",
      installment_amount: "",
      num_terms: "12",
      frequency: "monthly",
      start_date: `${year}-01-01`
    });
    setEditingPlan(null);
  };

  // Open add plan dialog
  const openAddPlan = () => {
    resetPlanForm();
    setShowAddPlan(true);
  };

  // Open edit plan dialog
  const openEditPlan = (plan) => {
    setPlanForm({
      creditor_name: plan.creditor_name,
      installment_amount: String(plan.installment_amount),
      num_terms: String(plan.num_terms),
      frequency: plan.frequency,
      start_date: plan.start_date
    });
    setEditingPlan(plan);
    setShowAddPlan(true);
  };

  // Save plan (create or update)
  const handleSavePlan = async () => {
    // Validate
    if (!planForm.creditor_name.trim()) {
      toast.error("Please enter a creditor name");
      return;
    }
    if (!planForm.installment_amount || parseFloat(planForm.installment_amount) < 1) {
      toast.error("Installment amount must be at least €1");
      return;
    }
    if (!planForm.num_terms || parseInt(planForm.num_terms) < 1) {
      toast.error("Number of terms must be at least 1");
      return;
    }
    
    try {
      const payload = {
        creditor_name: planForm.creditor_name.trim(),
        installment_amount: parseFloat(planForm.installment_amount),
        num_terms: parseInt(planForm.num_terms),
        frequency: planForm.frequency,
        start_date: planForm.start_date
      };
      
      if (editingPlan) {
        await axios.patch(`${API}/repayment-plans/${editingPlan.id}`, payload);
        toast.success("Plan updated successfully");
      } else {
        await axios.post(`${API}/repayment-plans`, payload);
        toast.success("Plan created successfully");
      }
      
      setShowAddPlan(false);
      resetPlanForm();
      await fetchData();
    } catch (err) {
      console.error("Error saving plan:", err);
      toast.error(err.response?.data?.detail || "Failed to save plan");
    }
  };

  // Delete plan
  const handleDeletePlan = async (plan) => {
    if (!window.confirm(`Delete repayment plan for "${plan.creditor_name}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/repayment-plans/${plan.id}`);
      toast.success("Plan deleted successfully");
      await fetchData();
    } catch (err) {
      console.error("Error deleting plan:", err);
      toast.error("Failed to delete plan");
    }
  };

  // Regenerate schedule
  const handleRegenerate = async () => {
    if (!window.confirm("This will clear all manual overrides for this year. Continue?")) {
      return;
    }
    
    try {
      await axios.post(`${API}/repayments/${year}/regenerate`);
      toast.success("Schedule regenerated");
      await fetchData();
    } catch (err) {
      console.error("Error regenerating:", err);
      toast.error("Failed to regenerate schedule");
    }
  };

  // Save manual overrides
  const handleSave = async () => {
    if (!hasChanges) return;
    
    setSaving(true);
    try {
      const bulkEntries = Object.entries(modifiedEntries).map(([key, amount]) => {
        const [plan_id, date] = key.split('_');
        return { plan_id, date, amount };
      });
      
      await axios.patch(`${API}/repayment-schedule/bulk`, { entries: bulkEntries });
      toast.success("Schedule saved successfully");
      setModifiedEntries({});
      await fetchData();
    } catch (err) {
      console.error("Error saving schedule:", err);
      toast.error("Failed to save schedule");
    } finally {
      setSaving(false);
    }
  };

  // Discard changes
  const handleDiscard = () => {
    setModifiedEntries({});
    setEntries(JSON.parse(JSON.stringify(originalEntries)));
    toast.info("Changes discarded");
  };

  // Format currency
  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return `${d.getDate().toString().padStart(2, '0')} ${MONTHS[d.getMonth()]}`;
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="error-state">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
          <h2 className="text-2xl font-semibold mb-2">Something went wrong</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={fetchData} data-testid="retry-button">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="repayments-page">
      {/* Header */}
      <header className="border-b bg-white shrink-0">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="page-title">
                Repayments
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Manage debt repayment schedules across creditors
              </p>
              <LastUpdated page="repayments" />
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Annual Repayments</p>
              <p className="text-2xl font-semibold text-red-600" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {formatCurrency(annualTotal)}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Year Navigator & Controls */}
      <div className="border-b bg-white/80 backdrop-blur sticky top-0 z-30 shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={goToPrevYear}
                className="rounded-full"
                data-testid="prev-year-button"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              <h2 className="text-xl font-semibold min-w-[100px] text-center" data-testid="current-year">
                {year}
              </h2>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={goToNextYear}
                className="rounded-full"
                data-testid="next-year-button"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="text-sm text-muted-foreground hidden md:block">
                {plans.length} creditors • {datesWithPayments.length} payment dates
              </div>
              
              {plans.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRegenerate}
                  className="rounded-full"
                  data-testid="regenerate-button"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Regenerate
                </Button>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={openAddPlan}
                className="rounded-full"
                data-testid="add-plan-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Creditor
              </Button>
              
              {year !== currentYear && (
                <Button
                  variant="outline"
                  onClick={goToCurrentYear}
                  className="rounded-full"
                  data-testid="current-year-button"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {currentYear}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-auto px-6 py-4">
        <div className="max-w-4xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-state">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : plans.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-state">
            <RotateCcw className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No repayment plans</h3>
            <p className="text-muted-foreground mb-4">Add your first creditor to start tracking repayments.</p>
            <Button onClick={openAddPlan} data-testid="add-first-plan">
              <Plus className="w-4 h-4 mr-2" />
              Add First Creditor
            </Button>
          </div>
        ) : (
          <>
            {/* Creditor Pills */}
            <div className="flex flex-wrap gap-2 mb-4">
              {plans.map(plan => (
                <div
                  key={plan.id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm"
                  style={{ borderColor: plan.color, backgroundColor: `${plan.color}10` }}
                >
                  <span 
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: plan.color }}
                  />
                  <span className="font-medium">{plan.creditor_name}</span>
                  <span className="text-muted-foreground">
                    {formatCurrency(plan.installment_amount)}/term × {plan.num_terms}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 ml-1"
                    onClick={() => openEditPlan(plan)}
                    data-testid={`edit-plan-${plan.id}`}
                  >
                    <Edit2 className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 text-destructive hover:text-destructive"
                    onClick={() => handleDeletePlan(plan)}
                    data-testid={`delete-plan-${plan.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Schedule Table */}
            {datesWithPayments.length === 0 ? (
              <div className="bg-white border rounded-xl p-8 text-center text-muted-foreground">
                No payments scheduled for {year}. Check that your start dates fall within this year.
              </div>
            ) : (
              <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="repayments-table">
                    <thead className="bg-muted/30 border-b sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider w-[80px] min-w-[80px] sticky left-0 bg-muted/30 z-10">
                          Date
                        </th>
                        {plans.map(plan => (
                          <th 
                            key={plan.id}
                            className="text-center px-3 py-2.5 text-xs font-medium uppercase tracking-wider min-w-[120px]"
                            style={{ color: plan.color }}
                          >
                            {plan.creditor_name}
                          </th>
                        ))}
                        <th className="text-right px-3 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider w-[90px] min-w-[90px] sticky right-0 bg-muted/30 z-10">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {datesWithPayments.map((dateStr, rowIndex) => (
                        <tr 
                          key={dateStr}
                          className={`border-b last:border-b-0 transition-colors hover:bg-muted/20 ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-muted/5'}`}
                          data-testid={`row-${dateStr}`}
                        >
                          <td className="px-3 py-1.5 font-medium text-sm sticky left-0 bg-inherit z-10">
                            {formatDate(dateStr)}
                          </td>
                          {plans.map(plan => {
                            const key = `${plan.id}_${dateStr}`;
                            const entry = entryMap[key];
                            const hasEntry = !!entry;
                            const isModified = key in modifiedEntries;
                            const isManual = entry?.is_manual_override || isModified;
                            
                            return (
                              <td key={plan.id} className="px-3 py-1.5 text-center min-w-[120px]">
                                {hasEntry ? (
                                  <Input
                                    type="text"
                                    inputMode="decimal"
                                    value={formatCurrencyInput(isModified ? modifiedEntries[key] : entry.amount)}
                                    onChange={(e) => handleAmountChange(plan.id, dateStr, parseCurrencyInput(e.target.value))}
                                    onFocus={(e) => {
                                      const val = isModified ? modifiedEntries[key] : entry.amount;
                                      e.target.value = val && val !== 0 ? val.toString().replace(".", ",") : "";
                                      e.target.select();
                                    }}
                                    onBlur={(e) => {
                                      e.target.value = formatCurrencyInput(parseCurrencyInput(e.target.value));
                                    }}
                                    className={`h-7 text-xs text-right font-mono w-full max-w-[100px] mx-auto ${isManual ? 'bg-amber-50 border-amber-200' : ''}`}
                                    style={{ color: plan.color }}
                                    data-testid={`amount-${plan.id}-${dateStr}`}
                                  />
                                ) : (
                                  <div className="h-7 flex items-center justify-center text-muted-foreground text-xs">
                                    —
                                  </div>
                                )}
                              </td>
                            );
                          })}
                          <td className="px-3 py-1.5 text-right font-mono font-semibold text-sm sticky right-0 bg-inherit z-10 text-red-600">
                            {formatCurrency(dateTotals[dateStr] || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-muted/50 border-t-2">
                      <tr>
                        <td className="px-3 py-2.5 font-semibold text-sm sticky left-0 bg-muted/50 z-10">
                          Total
                        </td>
                        {plans.map(plan => {
                          const planTotal = datesWithPayments.reduce((sum, dateStr) => {
                            const key = `${plan.id}_${dateStr}`;
                            const entry = entryMap[key];
                            return sum + (entry ? parseFloat(entry.amount) || 0 : 0);
                          }, 0);
                          return (
                            <td key={plan.id} className="px-3 py-2.5 text-center font-mono font-semibold text-sm min-w-[120px]" style={{ color: plan.color }}>
                              {formatCurrency(planTotal)}
                            </td>
                          );
                        })}
                        <td className="px-3 py-2.5 text-right font-mono font-bold text-red-600 sticky right-0 bg-muted/50 z-10">
                          {formatCurrency(annualTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
        </div>
      </main>

      {/* Floating Action Bar */}
      {hasChanges && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white border rounded-full shadow-lg px-6 py-3 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4" data-testid="action-bar">
          <span className="text-sm text-muted-foreground">
            {Object.keys(modifiedEntries).length} manual overrides
          </span>
          <Button variant="outline" onClick={handleDiscard} className="rounded-full" data-testid="discard-button">
            <X className="w-4 h-4 mr-2" />
            Discard
          </Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-full bg-primary text-primary-foreground" data-testid="save-button">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Schedule"}
          </Button>
        </div>
      )}

      {/* Add/Edit Plan Dialog */}
      <Dialog open={showAddPlan} onOpenChange={setShowAddPlan}>
        <DialogContent className="sm:max-w-md" data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle>{editingPlan ? "Edit Repayment Plan" : "Add Repayment Plan"}</DialogTitle>
            <DialogDescription>
              Configure the repayment schedule for a creditor.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="creditor-name">Creditor Name</Label>
              <Input
                id="creditor-name"
                value={planForm.creditor_name}
                onChange={(e) => setPlanForm(p => ({ ...p, creditor_name: e.target.value }))}
                placeholder="e.g., Bank ABC, Lease Company"
                data-testid="creditor-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="installment-amount">Amount per Term (€)</Label>
                <Input
                  id="installment-amount"
                  type="text"
                  inputMode="decimal"
                  value={formatCurrencyInput(planForm.installment_amount)}
                  onChange={(e) => setPlanForm(p => ({ ...p, installment_amount: parseCurrencyInput(e.target.value) }))}
                  onFocus={(e) => {
                    const val = planForm.installment_amount || 0;
                    e.target.value = val !== 0 ? val.toString().replace(".", ",") : "";
                    e.target.select();
                  }}
                  onBlur={(e) => {
                    e.target.value = formatCurrencyInput(parseCurrencyInput(e.target.value));
                  }}
                  placeholder="1.000"
                  data-testid="installment-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="num-terms">Number of Terms</Label>
                <Input
                  id="num-terms"
                  type="number"
                  min="1"
                  max="1200"
                  value={planForm.num_terms}
                  onChange={(e) => setPlanForm(p => ({ ...p, num_terms: e.target.value }))}
                  placeholder="12"
                  data-testid="num-terms-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="frequency">Frequency</Label>
                <Select
                  value={planForm.frequency}
                  onValueChange={(val) => setPlanForm(p => ({ ...p, frequency: val }))}
                >
                  <SelectTrigger id="frequency" data-testid="frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={planForm.start_date}
                  onChange={(e) => setPlanForm(p => ({ ...p, start_date: e.target.value }))}
                  data-testid="start-date-input"
                />
              </div>
            </div>
            {planForm.installment_amount && planForm.num_terms && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <p className="text-muted-foreground">
                  Total repayment: <strong>{formatCurrency(parseFloat(planForm.installment_amount) * parseInt(planForm.num_terms))}</strong>
                  {' '}over <strong>{planForm.num_terms}</strong> {planForm.frequency} payments
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPlan(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePlan} data-testid="save-plan-button">
              {editingPlan ? "Update Plan" : "Create Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RepaymentsPage;
